import type { components } from "@brandex/api-client";
import { formatDateTime } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Spinner, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";

import { useWebsites } from "../data";
import { fieldError, QueryError } from "../views";

type QueueRow = components["schemas"]["StaffTemplateRequestDto"];
type Status = QueueRow["request"]["status"];
type DesignBody = components["schemas"]["CustomDesignBody"];
type Layout = Exclude<DesignBody["base"], "custom">;
type Section = DesignBody["sections"][number];

const STATUS: Record<Status, { label: string; tone: "warning" | "accent" | "success" | "neutral" }> = {
  open: { label: "New", tone: "warning" },
  in_progress: { label: "In design", tone: "accent" },
  delivered: { label: "Delivered", tone: "success" },
  declined: { label: "Declined", tone: "neutral" },
  cancelled: { label: "Withdrawn", tone: "neutral" },
};
export const LAYOUTS: readonly { value: Layout; label: string }[] = [
  { value: "modern", label: "Modern" },
  { value: "classic", label: "Classic" },
  { value: "minimal", label: "Minimal" },
  { value: "editorial", label: "Editorial" },
  { value: "clinical", label: "Clinical" },
  { value: "elegant", label: "Elegant" },
  { value: "bold", label: "Bold" },
  { value: "warm", label: "Warm" },
  { value: "spotlight", label: "Spotlight" },
];
const SECTIONS: Record<Section, string> = { about: "About", posts: "Posts", book: "Booking", chambers: "Chambers", faq: "FAQ" };
/** Always on the page (the server refuses a design without them). */
const REQUIRED: readonly Section[] = ["book", "chambers"];
const FONTS: readonly { value: DesignBody["heading_font"]; label: string; stack: string }[] = [
  { value: "sans", label: "Geometric sans", stack: "var(--font-jakarta), sans-serif" },
  { value: "serif", label: "Classic serif", stack: "var(--font-serif, Georgia), Georgia, serif" },
  { value: "humanist", label: "Humanist sans", stack: "var(--font-inter), sans-serif" },
];
const CORNERS: Record<DesignBody["corners"], string> = { sharp: "0.375rem", soft: "1.25rem", round: "2rem" };

// ------------------------------------------------------- colour checks

function channel(hex: string, i: number): number {
  const c = parseInt(hex.slice(1 + 2 * i, 3 + 2 * i), 16) / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#rrggbb` (same formula as the server). */
export function luminance(hex: string): number {
  return 0.2126 * channel(hex, 0) + 0.7152 * channel(hex, 1) + 0.0722 * channel(hex, 2);
}

/** WCAG contrast ratio, 1–21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

export const isHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

/** The checks the server applies, so the editor can show them live. */
export function designChecks(d: Pick<DesignBody, "primary" | "surface" | "ink">) {
  const valid = isHex(d.primary) && isHex(d.surface) && isHex(d.ink);
  return {
    valid,
    text: valid ? contrast(d.ink, d.surface) : 0,
    button: valid ? contrast(d.primary, "#ffffff") : 0,
    light: valid ? luminance(d.surface) >= 0.75 : false,
  };
}

/**
 * Custom website templates (F-07): the website team's queue of doctors'
 * requests, and the design editor that delivers a practice's own template
 * (a built-in layout plus colours, heading type, corners and section order,
 * checked for readable contrast). Needs `platform.websites.manage`.
 */
export function TemplatesPage() {
  const [status, setStatus] = useState<"" | Status>("");
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Website templates</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Doctors ask for a custom template from their Brand kit. Design it here: pick the layout it builds on, the practice's colours, heading type, corners and section order.
          Colours are checked for readable text before saving. Deliver the request once the design is set.
        </p>
      </header>
      <Queue status={status} onStatus={setStatus} onDesign={setEditing} />
      <DesignEditor orgId={editing} onOrg={setEditing} />
    </div>
  );
}

function Queue({ status, onStatus, onDesign }: { status: "" | Status; onStatus: (s: "" | Status) => void; onDesign: (org: string) => void }) {
  const { api } = useBrandex();
  const list = useQuery<QueueRow[]>({
    queryKey: ["admin", "template-requests", status],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/template-requests", { params: { query: status === "" ? {} : { status } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-3">
        <div>
          <CardTitle>Requests</CardTitle>
          <CardDescription>Newest first.</CardDescription>
        </div>
        <Field label="Status">
          <Select className="min-w-44" value={status} onChange={(e) => onStatus(e.target.value as "" | Status)}>
            <option value="">Any status</option>
            {(Object.keys(STATUS) as Status[]).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
          </Select>
        </Field>
      </CardHeader>
      <CardContent className="grid gap-3">
        {list.isPending ? <Spinner label="Loading…" /> : null}
        {list.isError ? <QueryError error={list.error} /> : null}
        {list.data?.length === 0 ? <p className="text-sm text-muted-foreground">No requests.</p> : null}
        {list.data?.map((row) => <RequestRow key={row.request.id} row={row} onDesign={onDesign} />)}
      </CardContent>
    </Card>
  );
}

function RequestRow({ row, onDesign }: { row: QueueRow; onDesign: (org: string) => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const r = row.request;
  const [note, setNote] = useState("");
  const name = row.doctor_name ?? r.organization_id;
  const move = useMutation({
    mutationFn: async (to: "in_progress" | "delivered" | "declined") => {
      await api.http.POST("/v1/admin/template-requests/{request_id}/status", { params: { path: { request_id: r.id } }, body: { status: to, note: note.trim() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "template-requests"] }),
  });
  const open = r.status === "open" || r.status === "in_progress";
  return (
    <article className="grid gap-3 rounded-[var(--radius-lg)] border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{name}</span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
          {formatDateTime("en", r.created_at)}
        </span>
      </div>
      <p className="whitespace-pre-line text-sm">{r.brief}</p>
      {r.references.length > 0 ? (
        <ul className="flex flex-wrap gap-3 text-sm">
          {r.references.map((u) => <li key={u}><a className="text-primary hover:underline" href={u} target="_blank" rel="noopener noreferrer">{u}</a></li>)}
        </ul>
      ) : null}
      {r.staff_note ? <p className="text-sm text-muted-foreground">Reply: {r.staff_note}</p> : null}
      <p className="text-xs text-muted-foreground">{row.design ? `Design set: ${row.design.name}` : "No design set yet."}</p>
      {open ? (
        <div className="grid gap-2">
          <Input aria-label={`Note to ${name}`} placeholder="Note to the doctor (needed to decline)" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          {move.isError && fieldError(move.error, "note") === undefined ? <QueryError error={move.error} /> : null}
          {fieldError(move.error, "note") ? <p className="text-xs text-destructive">{fieldError(move.error, "note")}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onDesign(r.organization_id)}>{row.design ? "Edit design" : "Design it"}</Button>
            {r.status === "open" ? <Button size="sm" variant="ghost" loading={move.isPending && move.variables === "in_progress"} onClick={() => move.mutate("in_progress")}>Start</Button> : null}
            <Button size="sm" disabled={!row.design} loading={move.isPending && move.variables === "delivered"} onClick={() => move.mutate("delivered")} aria-label={`Deliver to ${name}`}>Deliver</Button>
            <Button size="sm" variant="ghost" disabled={note.trim() === ""} loading={move.isPending && move.variables === "declined"} onClick={() => move.mutate("declined")} aria-label={`Decline ${name}`}>Decline</Button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

const BLANK: DesignBody = {
  name: "",
  base: "modern",
  primary: "#1f6fff",
  surface: "#ffffff",
  ink: "#0b1430",
  heading_font: "sans",
  corners: "soft",
  sections: ["about", "posts", "book", "chambers", "faq"],
  activate: false,
};

function DesignEditor({ orgId, onOrg }: { orgId: string | null; onOrg: (org: string) => void }) {
  const { api } = useBrandex();
  const sites = useWebsites();
  const current = useQuery({
    queryKey: ["admin", "custom-design", orgId],
    enabled: orgId !== null,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/orgs/{org_id}/custom-design", { params: { path: { org_id: orgId ?? "" } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Design editor</CardTitle>
        <CardDescription>Choose a practice, then build its template. Saving replaces any design it already has.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        <Field label="Practice">
          <Select className="max-w-md" value={orgId ?? ""} onChange={(e) => e.target.value && onOrg(e.target.value)}>
            <option value="">Choose a practice…</option>
            {(sites.data ?? []).map((s) => <option key={s.organization_id} value={s.organization_id}>{s.doctor_name ?? s.organization_id}</option>)}
          </Select>
        </Field>
        {orgId !== null && current.isPending ? <Spinner label="Loading…" /> : null}
        {current.isError ? <QueryError error={current.error} /> : null}
        {orgId !== null && current.data !== undefined ? (
          <DesignForm
            key={`${orgId}:${current.data.design?.updated_at ?? "new"}`}
            orgId={orgId}
            usesCustom={current.data.template === "custom"}
            initial={current.data.design ? { ...current.data.design, base: current.data.design.base === "custom" ? "modern" : current.data.design.base, activate: current.data.template === "custom" } : BLANK}
            hasDesign={current.data.design != null}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function DesignForm({ orgId, initial, hasDesign, usesCustom }: { orgId: string; initial: DesignBody; hasDesign: boolean; usesCustom: boolean }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [d, setD] = useState<DesignBody>(initial);
  const checks = designChecks(d);
  const set = (patch: Partial<DesignBody>) => {
    save.reset();
    setD((x) => ({ ...x, ...patch }));
  };
  const save = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.PUT("/v1/admin/orgs/{org_id}/custom-design", { params: { path: { org_id: orgId } }, body: { ...d, name: d.name.trim() } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "custom-design", orgId] });
      void qc.invalidateQueries({ queryKey: ["admin", "template-requests"] });
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      await api.http.DELETE("/v1/admin/orgs/{org_id}/custom-design", { params: { path: { org_id: orgId } } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "custom-design", orgId] });
      void qc.invalidateQueries({ queryKey: ["admin", "template-requests"] });
    },
  });
  const move = (i: number, by: -1 | 1) => {
    const next = [...d.sections];
    const j = i + by;
    const [a, b] = [next[i], next[j]];
    if (a === undefined || b === undefined) return;
    next[i] = b;
    next[j] = a;
    set({ sections: next });
  };
  const toggle = (s: Section, on: boolean) => set({ sections: on ? [...d.sections, s] : d.sections.filter((x) => x !== s) });
  const ok = checks.valid && checks.text >= 4.5 && checks.button >= 3 && checks.light && d.name.trim().length >= 2;
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); if (ok) save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Design name" hint="Shown to the doctor, e.g. their clinic's name." error={fieldError(save.error, "name")}>
            <Input maxLength={60} value={d.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Builds on layout" error={fieldError(save.error, "base")}>
            <Select value={d.base} onChange={(e) => set({ base: e.target.value as Layout })}>
              {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </Select>
          </Field>
        </div>
        <fieldset className="grid gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-medium">Colours</legend>
          <ColourField label="Primary (buttons)" value={d.primary} onChange={(primary) => set({ primary })} error={fieldError(save.error, "primary")} />
          <ColourField label="Page background" value={d.surface} onChange={(surface) => set({ surface })} error={fieldError(save.error, "surface")} />
          <ColourField label="Text" value={d.ink} onChange={(ink) => set({ ink })} error={fieldError(save.error, "ink")} />
        </fieldset>
        <ul className="grid gap-1 text-sm" aria-label="Readability checks">
          <Check ok={checks.text >= 4.5} label={`Text on background ${checks.text.toFixed(2)}:1 (needs 4.5)`} />
          <Check ok={checks.button >= 3} label={`White button text ${checks.button.toFixed(2)}:1 (needs 3)`} />
          <Check ok={checks.light} label="Background is light" />
        </ul>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Heading type">
            <Select value={d.heading_font} onChange={(e) => set({ heading_font: e.target.value as DesignBody["heading_font"] })}>
              {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
          </Field>
          <Field label="Corners">
            <Select value={d.corners} onChange={(e) => set({ corners: e.target.value as DesignBody["corners"] })}>
              <option value="sharp">Sharp</option>
              <option value="soft">Soft</option>
              <option value="round">Round</option>
            </Select>
          </Field>
        </div>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">Sections, in page order</legend>
          <ol className="grid gap-1.5">
            {d.sections.map((s, i) => (
              <li key={s} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
                <span>{i + 1}. {SECTIONS[s]}{REQUIRED.includes(s) ? <span className="text-muted-foreground"> (always shown)</span> : null}</span>
                <span className="flex gap-1">
                  <Button type="button" size="icon" variant="ghost" aria-label={`Move ${SECTIONS[s]} up`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" aria-hidden /></Button>
                  <Button type="button" size="icon" variant="ghost" aria-label={`Move ${SECTIONS[s]} down`} disabled={i === d.sections.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" aria-hidden /></Button>
                  {REQUIRED.includes(s) ? null : (
                    <Button type="button" size="icon" variant="ghost" aria-label={`Hide ${SECTIONS[s]}`} onClick={() => toggle(s, false)}><XCircle className="h-4 w-4" aria-hidden /></Button>
                  )}
                </span>
              </li>
            ))}
          </ol>
          {(Object.keys(SECTIONS) as Section[]).filter((s) => !d.sections.includes(s)).map((s) => (
            <Button key={s} type="button" size="sm" variant="outline" className="w-fit" onClick={() => toggle(s, true)}>Add {SECTIONS[s]}</Button>
          ))}
          {fieldError(save.error, "sections") ? <p className="text-xs text-destructive">{fieldError(save.error, "sections")}</p> : null}
        </fieldset>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-0.5" checked={d.activate} onChange={(e) => set({ activate: e.target.checked })} />
          <span>
            <span className="font-medium">Switch the doctor's site to this design now</span>
            <span className="block text-muted-foreground">{usesCustom ? "The site already uses its custom design." : "Leave off to let the doctor choose it in their Brand kit."}</span>
          </span>
        </label>
        {save.isSuccess ? <Alert tone="success">Design saved{d.activate ? " and live on the doctor's site" : ""}.</Alert> : null}
        {save.isError && ["name", "base", "primary", "surface", "ink", "sections"].every((f) => fieldError(save.error, f) === undefined) ? <QueryError error={save.error} /> : null}
        {remove.isError ? <QueryError error={remove.error} /> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={!ok} loading={save.isPending}>Save design</Button>
          {hasDesign ? <Button type="button" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>Remove design</Button> : null}
        </div>
      </form>
      <DesignPreview d={d} />
    </div>
  );
}

function ColourField({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string | undefined }) {
  return (
    <Field label={label} error={error ?? (isHex(value) ? undefined : "Use #rrggbb")}>
      <span className="flex items-center gap-2">
        <input type="color" aria-label={`${label} picker`} className="h-9 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent" value={isHex(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} />
        <Input className="font-mono" maxLength={7} value={value} onChange={(e) => onChange(e.target.value.trim())} />
      </span>
    </Field>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={cn("flex items-center gap-2", ok ? "text-emerald-600 dark:text-emerald-400" : "text-destructive")}>
      {ok ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <XCircle className="h-4 w-4" aria-hidden />}
      {label}
    </li>
  );
}

/** A small rendering of the doctor's site header with the design applied. */
function DesignPreview({ d }: { d: DesignBody }) {
  const font = FONTS.find((f) => f.value === d.heading_font)?.stack;
  const radius = CORNERS[d.corners];
  const colour = (v: string, fallback: string) => (isHex(v) ? v : fallback);
  return (
    <aside aria-label="Preview" className="grid content-start gap-2">
      <p className="text-sm font-medium">Preview</p>
      <div className="overflow-hidden border border-border shadow-sm" style={{ background: colour(d.surface, "#ffffff"), color: colour(d.ink, "#0b1430"), borderRadius: radius }}>
        <div className="flex items-center justify-between border-b px-3 py-2 text-xs" style={{ borderColor: `${colour(d.ink, "#0b1430")}22` }}>
          <span className="font-semibold">Dr. Sample Doctor</span>
          <span className="rounded-full px-2 py-0.5 text-white" style={{ background: colour(d.primary, "#1f6fff") }}>EN</span>
        </div>
        <div className="grid gap-3 p-4">
          <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: colour(d.primary, "#1f6fff") }}>Cardiology</span>
          <span className="text-2xl font-bold leading-tight" style={{ fontFamily: font }}>Dr. Sample Doctor</span>
          <span className="text-xs opacity-80">Consultant cardiologist helping patients prevent heart disease.</span>
          <span className="inline-flex w-fit px-3 py-2 text-xs font-semibold text-white" style={{ background: colour(d.primary, "#1f6fff"), borderRadius: radius }}>Book an appointment</span>
          <span className="grid grid-cols-2 gap-2">
            {[0, 1].map((i) => <span key={i} className="h-10 border" style={{ borderRadius: radius, borderColor: `${colour(d.ink, "#0b1430")}22`, background: `color-mix(in oklab, ${colour(d.primary, "#1f6fff")} 8%, ${colour(d.surface, "#ffffff")})` }} />)}
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Order: {d.sections.map((s) => SECTIONS[s]).join(" → ")}</p>
    </aside>
  );
}
